/**
 * Utility functions for AHU design calculations
 */

import { IBotAccessory, IBotData } from '../../models';

/**
 * Interpolates/extrapolates optimal sizes based on a graph of optimal values
 * @param optimals - Array of optimal size data points
 * @param seeker - Target airflow value to find optimal size for
 * @param options - Configuration options
 * @returns Tuple of [airflow, optimal_width, optimal_height]
 */
export function interpolateExtrapolate(
  optimals:
    | Array<[number, number, number]>
    | Array<{
        airflow: number;
        optimal_width?: number;
        optimal_height?: number;
        [key: string]: any;
      }>,
  seeker: number,
  { sort = true }: { sort?: boolean } = {},
): [number, number, number] {
  /**
   * graph - array of arrays - [[0,0,0], [1,1,2]...]
   * or array of objects - [{airflow: 10000, optimal_width: 70, optimal_height: 52}, ...]
   *
   *    *       *
   *   /  -\   X
   *  /   -/  /
   * * x     *
   *
   * x -> seeker
   */

  if (!optimals || optimals.length === 0) {
    return [seeker, 0, 0];
  }

  // Convert objects to arrays if needed
  let graph: Array<[number, number, number]>;
  if (typeof optimals[0] === 'object' && !Array.isArray(optimals[0])) {
    // Convert object format to array format
    graph = (optimals as any[]).map((item: any) => [
      item.airflow || 0,
      item.optimal_width || 0,
      item.optimal_height || 0,
    ]);
  } else {
    graph = optimals as Array<[number, number, number]>;
  }

  if (sort) {
    // If you give sort=false, then you have to be sure that the graph is sorted
    graph = Array.from(graph); // Duplicating the array to prevent changes of original one
    graph.sort((a, b) => a[0] - b[0]);
  }

  if (graph.length === 0) {
    return [seeker, 0, 0];
  }

  if (graph.length === 1) {
    return graph[0];
  }

  // Interpolate a-b line to find point with first dimension = c
  function interpolate(
    a: [number, number, number],
    b: [number, number, number],
    c: number,
  ): [number, number, number] {
    let f = (c - a[0]) / (b[0] - a[0]);
    return a.map((_, i) => (b[i] - a[i]) * f + a[i]) as [
      number,
      number,
      number,
    ];
  }

  if (seeker < graph[0][0]) {
    return interpolate(graph[0], graph[1], seeker);
  }

  if (seeker > graph[graph.length - 1][0]) {
    return interpolate(
      graph[graph.length - 2],
      graph[graph.length - 1],
      seeker,
    );
  }

  for (let i = 0; i < graph.length - 1; i++) {
    if (seeker >= graph[i][0] && seeker <= graph[i + 1][0]) {
      return interpolate(graph[i], graph[i + 1], seeker);
    }
  }

  // Fallback - return last point
  return graph[graph.length - 1];
}

/**
 * Transforms a value to the nearest multiple of increment
 * @param value - Value to transform
 * @param increment - Increment to round to
 * @returns Rounded value
 */
export function transformValue(value: number, increment: number): number {
  const roundedValue = Math.ceil(value / increment) * increment;
  return parseFloat(roundedValue.toFixed(10));
}

/**
 * Rounds a value based on increment
 * @param value - Value to round
 * @param increment - Increment to round to
 * @returns Rounded value
 */
export function roundBasedOnArguments(
  value: number,
  increment: number,
): number {
  if (value % increment) return value - (value % increment) + increment;
  return value;
}
// For Opening
export const MIN_CLEAR_MAP: Record<
  string,
  {
    ups: string;
    downs: string;
    left: string;
    right: string;
    ups_down: string;
  }
> = {
  '1': {
    ups: 'min_clear_if_on_flr_from_ups',
    downs: 'min_clear_if_on_flr_from_downs',
    left: 'min_clear_if_on_flr_from_sides',
    right: 'min_clear_if_on_flr_from_side_walls',
    ups_down: 'min_clear_if_on_flr_from_section_ups_downs',
  },
  '2': {
    ups: 'min_clear_if_on_rf_from_ups',
    downs: 'min_clear_if_on_rf_from_downs',
    left: 'min_clear_if_on_rf_from_sides',
    right: 'min_clear_if_on_rf_from_side_walls',
    ups_down: 'min_clear_if_on_rf_from_section_ups_downs',
  },
};

export function normalizeEven(value: number): number {
  return value % 2 === 0 ? value : value - 1;
}

export function calculateInitialSize(airflow: number) {
  const x = Math.floor(Math.sqrt((144 * airflow) / 2000));
  return {
    width: normalizeEven(2 * x),
    height: normalizeEven(x),
  };
}

export function resolveAirflow(
  accessory: IBotAccessory,
  botSection: any,
  botData: IBotData,
) {
  const isReturn = accessory.return_airflow;
  const isOutside = accessory.outside_airflow;
  return {
    airflow: isReturn ?  botData.ra_volume_max : isOutside ? botData.oa_volume_max : botSection.airflow,
    air_stream_id: isReturn ? 2 : isOutside ? 3 : botSection.air_stream_id,
  };
}

export function calculateExtraClearances(
  opening: any,
  mounting_plane_id: number,
  isFloorOrRoof: boolean,
) {
  let extra_width = 0;
  let extra_height = 0;
  let extra_length = 0;

  // End wall
  if (!isFloorOrRoof) {
    const top = Math.max(
      opening.min_clear_if_on_end_wall_from_top || 0,
      opening.min_clear_if_on_end_wall_from_rf || 0,
    );
    const bottom = Math.max(
      opening.min_clear_if_on_end_wall_from_btm || 0,
      opening.min_clear_if_on_end_wall_from_flr || 0,
    );
    const side = Math.max(
      opening.min_clear_if_on_end_wall_from_sides || 0,
      opening.min_clear_if_on_end_wall_from_side_walls || 0,
    );

    return {
      extra_width: 2 * side,
      extra_height: 2 * Math.max(top, bottom),
      extra_length: 0,
    };
  }

  // Floor / Roof
  const clears = MIN_CLEAR_MAP[String(mounting_plane_id)];
  if (!clears) return { extra_width, extra_height, extra_length };

  const ups = opening[clears.ups] || 0;
  const downs = opening[clears.downs] || 0;
  const side =
    2 * Math.max(opening[clears.left] || 0, opening[clears.right] || 0);

  return {
    extra_width: side,
    extra_height: 0,
    extra_length:
      Math.max(ups, opening[clears.ups_down] || 0) +
      Math.max(downs, opening[clears.ups_down] || 0),
  };
}
